import { useState, useEffect } from 'react';
import { Skeleton } from '@solid/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Plus,
  Search,
  Filter,
  X,
  CalendarClock,
} from 'lucide-react';
import { discussionLabels } from '@/lib/discussion-labels';
import { discussionsApi, projectsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { CreateDiscussionModal } from '@/components/modals/CreateDiscussionModal';
import { DiscussionCard, Discussion } from '@/components/discussions/DiscussionCard';
import { MessageThreadTakeover } from '@/components/discussions/MessageThreadTakeover';
import { DiscussionStatus } from '@/components/ui/StatusDropdown';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { cn } from '@/lib/utils';

// Status filter options (agency-only)
const STATUS_FILTERS: Array<{ value: string; label: string; color?: string }> = [
  { value: 'all', label: 'All Discussions' },
  { value: 'waiting_on_solid', label: 'Waiting on Solid', color: 'bg-primary' },
  { value: 'open', label: 'Open', color: 'bg-grey-400' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-500' },
  { value: 'waiting_on_client', label: 'Waiting on Client', color: 'bg-primary' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-orange-500' },
  { value: 'closed', label: 'Closed', color: 'bg-grey-300' },
];

export function ProjectDiscussions() {
  const { projectId, typeSlug, threadId } = useParams<{ projectId: string; typeSlug?: string; threadId?: string }>();
  // A thread is open (as a takeover modal) when the URL carries a threadId.
  const listPath = typeSlug ? `/projects/${projectId}/custom/${typeSlug}` : `/projects/${projectId}/discussions`;
  const threadPath = (id: string) =>
    typeSlug ? `/projects/${projectId}/custom/${typeSlug}/${id}` : `/projects/${projectId}/discussions/${id}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);

  // Open create modal when navigated with openCreate state (e.g. from sidebar)
  useEffect(() => {
    if ((location.state as any)?.openCreate) {
      setShowCreateModal(true);
      nav(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, nav]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Get filter state from URL
  const archived = searchParams.get('archived') === 'true';
  const statusFilter = searchParams.get('status') || 'all';
  const page = parseInt(searchParams.get('page') || '1', 10);

  // Staff on THIS project: Solid anywhere, a partner agency on its own
  const { isProjectStaff: isAgencyMember } = useProjectRoles(projectId);

  // Fetch project for breadcrumb and archived check
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  const isArchived = !!project?.archivedAt;

  // Resolve type labels from project settings
  const typeConfig = typeSlug
    ? ((project?.settings as any)?.discussionTypes || []).find(
        (dt: any) => dt.slug === typeSlug
      )
    : undefined;
  const projectLabels = discussionLabels((project as { settings?: unknown } | undefined)?.settings);
  const typeLabel = typeConfig?.label || (typeSlug ? typeSlug : undefined);
  const typeLabelPlural = typeConfig?.labelPlural || typeLabel;

  // Fetch discussions
  const { data, isLoading, error } = useQuery({
    queryKey: ['discussions', projectId, page, archived, typeSlug],
    queryFn: () => discussionsApi.list(projectId!, page, archived, typeSlug),
    enabled: !!projectId,
  });

  // Scheduled (unpublished) discussions — agency members see the project's
  // queue; the API scopes non-admins to their own.
  const { data: scheduledData } = useQuery({
    queryKey: ['scheduled-discussions', projectId, typeSlug],
    queryFn: () => discussionsApi.list(projectId!, 1, false, typeSlug, true),
    enabled: !!projectId && !!isAgencyMember && !archived,
  });
  const scheduledDiscussions: Discussion[] = scheduledData?.data || [];

  // Status update mutation (handles all statuses including archived)
  const statusMutation = useMutation({
    mutationFn: ({ discussionId, status }: { discussionId: string; status: DiscussionStatus }) =>
      discussionsApi.updateStatus(discussionId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId, page, archived, typeSlug] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const handleStatusChange = (discussionId: string, status: DiscussionStatus) => {
    statusMutation.mutate({ discussionId, status });
  };

  const handleStatusFilterChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('status');
    } else {
      newParams.set('status', value);
    }
    newParams.delete('page'); // Reset to first page
    setSearchParams(newParams);
  };

  const handleArchivedToggle = (showArchived: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (showArchived) {
      newParams.set('archived', 'true');
    } else {
      newParams.delete('archived');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Filter discussions by status and search (client-side for now)
  const discussions = data?.data || [];
  const filteredDiscussions = discussions.filter((d: Discussion) => {
    // Status filter (agency only)
    if (isAgencyMember && statusFilter !== 'all') {
      const dStatus = d.discussionStatus || 'open';
      if (dStatus !== statusFilter) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const titleMatch = d.title?.toLowerCase().includes(query);
      const contentMatch = d.content?.toLowerCase().includes(query);
      const authorMatch = d.author?.name?.toLowerCase().includes(query);
      if (!titleMatch && !contentMatch && !authorMatch) return false;
    }

    return true;
  });

  const pagination = data?.pagination;

  return (
    <div className="w-full">
      {/* Filters Bar */}
      <Card padding="sm" className="mb-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* New Discussion Button - shown first on mobile for visibility */}
          {!isArchived && (
            <Button onClick={() => setShowCreateModal(true)} size="sm" className="order-first sm:order-last">
              <Plus className="w-4 h-4" />
              {typeLabel ? `New ${typeLabel}` : `New ${projectLabels.singular}`}
            </Button>
          )}

          {/* Search */}
          <div className="relative flex-1 min-w-[140px] sm:flex-none sm:w-64 order-last sm:order-first">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="input pl-10 pr-8 text-sm w-full"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status Filter (Agency Only) */}
          {isAgencyMember && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                showFilters || statusFilter !== 'all'
                  ? 'bg-accent/10 text-accent'
                  : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
              )}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filter</span>
              {statusFilter !== 'all' && (
                <span className="bg-accent text-white text-xs px-1.5 py-0.5 rounded-full">1</span>
              )}
            </button>
          )}

          {/* Archive Toggle */}
          <div className="flex rounded-lg bg-grey-100 p-0.5">
            <button
              onClick={() => handleArchivedToggle(false)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                !archived ? 'bg-white text-secondary shadow-sm' : 'text-grey-500 hover:text-secondary'
              )}
            >
              Open
            </button>
            <button
              onClick={() => handleArchivedToggle(true)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                archived ? 'bg-white text-secondary shadow-sm' : 'text-grey-500 hover:text-secondary'
              )}
            >
              Archived
            </button>
          </div>

          {/* Spacer - desktop only */}
          <div className="hidden sm:block flex-1" />
        </div>

        {/* Status Filter Pills (Agency Only) */}
        {isAgencyMember && showFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-grey-100">
            <span className="text-xs text-grey-500 font-medium uppercase tracking-wider">Status:</span>
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => handleStatusFilterChange(filter.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                  statusFilter === filter.value
                    ? 'bg-secondary text-white'
                    : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                )}
              >
                {filter.color && (
                  <span className={cn('w-2 h-2 rounded-full', filter.color)} />
                )}
                {filter.label}
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Scheduled queue (agency members; authors see their own) */}
      {!archived && scheduledDiscussions.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 text-sm font-medium text-grey-500">
            <CalendarClock className="w-4 h-4 text-accent" />
            Scheduled ({scheduledDiscussions.length})
          </div>
          <div className="space-y-4">
            {scheduledDiscussions.map((discussion: Discussion) => (
              <DiscussionCard
                key={discussion.id}
                discussion={discussion}
                projectId={projectId!}
                isAgencyMember={isAgencyMember}
                discussionType={typeSlug}
                onOpen={(id) => nav(threadPath(id))}
                className="border-dashed border-accent/30"
              />
            ))}
          </div>
        </div>
      )}

      {/* Discussion List */}
      <div>
        {isLoading ? (
          <div className="space-y-4 max-w-4xl">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <Card>
            <EmptyState
              icon={MessageSquare}
              title="Error loading discussions"
              description="There was a problem loading the discussions. Please try again."
            />
          </Card>
        ) : filteredDiscussions.length === 0 ? (
          <Card>
          <EmptyState
            icon={MessageSquare}
            title={searchQuery || statusFilter !== 'all' ? `No ${(typeLabelPlural || projectLabels.plural).toLowerCase()} found` : `No ${(typeLabelPlural || projectLabels.plural).toLowerCase()} yet`}
            description={
              searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : archived
                ? `No archived ${(typeLabelPlural || projectLabels.plural).toLowerCase()} in this project.`
                : 'Start a conversation to collaborate with your team.'
            }
            action={
              !searchQuery && statusFilter === 'all' && !archived && !isArchived ? (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4" />
                  {typeLabel ? `Start the first ${typeLabel.toLowerCase()}` : 'Start the first discussion'}
                </Button>
              ) : undefined
            }
          />
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredDiscussions.map((discussion: Discussion) => (
              <DiscussionCard
                key={discussion.id}
                discussion={discussion}
                projectId={projectId!}
                isAgencyMember={isAgencyMember}
                onStatusChange={handleStatusChange}
                discussionType={typeSlug}
                onOpen={(id) => nav(threadPath(id))}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.total > pagination.limit && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('page', String(page - 1));
                setSearchParams(newParams);
              }}
            >
              Previous
            </Button>
            <span className="text-sm text-grey-500">
              Page {page} of {Math.ceil(pagination.total / pagination.limit)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= Math.ceil(pagination.total / pagination.limit)}
              onClick={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('page', String(page + 1));
                setSearchParams(newParams);
              }}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateDiscussionModal
        isOpen={showCreateModal}
        projectId={projectId!}
        onClose={() => setShowCreateModal(false)}
        discussionType={typeSlug}
        typeLabel={typeLabel}
      />

      {/* Discussion thread takeover (URL-driven — deep links land here too) */}
      {threadId && projectId && (
        <MessageThreadTakeover
          threadId={threadId}
          projectId={projectId}
          typeSlug={typeSlug}
          onClose={() => nav(listPath)}
        />
      )}
    </div>
  );
}
